import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { playLocal } from "../audio";
import { toast } from "../toast";
import type { Voice } from "../types";
import { Icons, IconButton, SearchIcon } from "./Icons";

/* The search box doubles as the picker's value: while you are not typing in
   it, it shows the selected voice, the way a native <select> would.
   `search === null` means "not searching".

   Rendered inside the composer's `voice` chip rather than as a card of its own:
   the list is long and the search is worth the room, but it is worth it for the
   two seconds you are choosing, not for the whole stream. */

interface Props {
  voices: Voice[];
  selected: Voice | null;
  onSelect: (voice: Voice) => void;
  onRefresh: () => void;
}

function matchesNeedle(voice: Voice, needle: string): boolean {
  if (!needle) return true;
  const haystack = [
    voice.name,
    voice.category,
    voice.description,
    ...Object.values(voice.labels || {}),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

export function VoicePicker({ voices, selected, onSelect, onRefresh }: Props) {
  const [search, setSearch] = useState<string | null>(null);
  const [cursor, setCursor] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const searching = search !== null;
  const needle = (search || "").toLowerCase().trim();

  const matches = useMemo(
    () => voices.filter((voice) => matchesNeedle(voice, needle)),
    [voices, needle],
  );

  // The list can shrink under the highlight while you keep typing.
  const at = cursor >= matches.length ? matches.length - 1 : cursor;

  /* Keep the highlighted row visible without ever scrolling the page itself. */
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const row =
      list.querySelector<HTMLElement>(".voice.cursor") ??
      list.querySelector<HTMLElement>(".voice.active");
    if (!row) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top - 4;
    else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight + 4;
    }
  }, [at, selected, matches]);

  function stopSearching() {
    setSearch(null);
    setCursor(-1);
  }

  function pick(voice: Voice) {
    onSelect(voice);
    stopSearching();
    inputRef.current?.blur();
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      stopSearching();
      event.currentTarget.blur();
      return;
    }
    if (event.key === "Enter") {
      const voice = matches[at] || (searching ? matches[0] : null);
      if (!voice) return;
      event.preventDefault();
      pick(voice);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    if (!matches.length) return;
    const step = event.key === "ArrowDown" ? 1 : -1;
    const from =
      at >= 0
        ? at
        : matches.findIndex((voice) => voice.voice_id === selected?.voice_id);
    setCursor(
      from < 0
        ? step > 0
          ? 0
          : matches.length - 1
        : (from + step + matches.length) % matches.length,
    );
  }

  const value = searching ? (search as string) : selected?.name || "";

  return (
    <div className="picker">
      <div className="picker-head">
        <span className="field-label">Voice</span>
        <span className="spacer"></span>
        <span className="muted">
          {needle ? `${matches.length} of ${voices.length}` : ""}
        </span>
        <IconButton
          icon="refresh"
          title="Re-fetch from ElevenLabs"
          onClick={onRefresh}
        />
      </div>

      {/* The box shows the selected voice until you start typing in it. */}
      <div className="voice-picker">
        <SearchIcon />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="voice-list"
          aria-autocomplete="list"
          aria-label="Voice"
          autoComplete="off"
          spellCheck={false}
          autoFocus
          placeholder="Search voices…"
          className={!searching && selected ? "chosen" : undefined}
          value={value}
          onFocus={(event) => event.target.select()}
          onBlur={stopSearching}
          onChange={(event) => {
            setSearch(event.target.value);
            setCursor(-1);
          }}
          onKeyDown={onKeyDown}
        />
        {searching && value && (
          <button
            type="button"
            className="icon-btn clear"
            title="Clear search"
            aria-label="Clear search"
            // mousedown would blur the box first and cancel the search
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setSearch("");
              setCursor(-1);
              inputRef.current?.focus();
            }}
          >
            {Icons.clear}
          </button>
        )}
      </div>

      {/* Clicking a row must not blur the box mid-click. */}
      <div
        className="voice-list"
        id="voice-list"
        role="listbox"
        aria-label="Voices"
        ref={listRef}
        onMouseDown={(event) => event.preventDefault()}
      >
        {matches.length === 0 ? (
          <div className="empty muted">No voices match.</div>
        ) : (
          matches.map((voice, index) => {
            const active = selected?.voice_id === voice.voice_id;
            return (
              <div
                key={voice.voice_id}
                className={
                  "voice" + (active ? " active" : "") + (index === at ? " cursor" : "")
                }
                role="option"
                aria-selected={active}
                onClick={() => pick(voice)}
              >
                <div className="name">{voice.name}</div>
                <button
                  type="button"
                  className="preview icon-btn"
                  title="Preview in your browser only"
                  aria-label="Preview voice"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!voice.preview_url) {
                      toast("No preview for this voice");
                      return;
                    }
                    playLocal(voice.preview_url);
                  }}
                >
                  {Icons.play}
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Everything the row does not show lives under the list instead. */}
      <div id="voice-detail" className="muted">
        {selected ? detail(selected) : ""}
      </div>
    </div>
  );
}

function detail(voice: Voice): string {
  const bits: string[] = [];
  if (voice.description) bits.push(voice.description);
  const labels = Object.entries(voice.labels || {})
    .map(([key, value]) => `${key}: ${value}`)
    .join(", ");
  if (labels) bits.push(labels);
  if (voice.category) bits.push(voice.category);
  bits.push(`id ${voice.voice_id}`);
  return bits.join(" · ");
}
